import { Provider } from '../../../models/Entities/Provider/Provider';
import { NewProvider } from '../../../models/Entities/Provider/NewProvider';
import { isValidTaxId, normalizeTaxId } from '../../../functions/ProviderInbox.Functions';

/*
 * Lógica pura del formulario de proveedor: normalización, validación y armado
 * de los comandos de alta y modificación. Sin React ni store, para testearla sola.
 */

/** Largo máximo de la razón social (el contador del campo lo muestra como "46 / 200"). */
export const BUSINESS_NAME_MAX = 200;

export type ProviderFormErrorKey = 'required' | 'tooLong';

export const normalizeBusinessName = (value: string | null | undefined): string => (value ?? '').replace(/\s+/g, ' ').trim();

export const validateBusinessName = (value: string | null | undefined): ProviderFormErrorKey | undefined => {
    const name = normalizeBusinessName(value);
    if (!name) return 'required';
    if (name.length > BUSINESS_NAME_MAX) return 'tooLong';
    return undefined;
};

/** Alta: el API sólo toma la razón social; nombre y apellido viajan vacíos como siempre. */
export const buildNewProvider = (businessName: string): NewProvider => ({
    BusinessName: normalizeBusinessName(businessName),
    firstName: '',
    lastName: ''
});

/** Modificación: se conserva todo el proveedor y sólo cambia la razón social de la persona. */
export const buildUpdatedProvider = (provider: Provider, businessName: string): Provider => ({
    ...provider,
    person: {
        ...provider.person,
        businessName: normalizeBusinessName(businessName)
    }
});

/** Hay cambios sin guardar: en alta, cualquier texto; en edición, un nombre distinto al cargado. */
export const isProviderFormDirty = (provider: Provider | undefined, businessName: string | undefined): boolean => {
    const current = normalizeBusinessName(businessName);
    if (!provider) return current.length > 0;
    return current !== normalizeBusinessName(provider.person?.businessName);
};

export type TaxIdErrorKey = 'format' | 'checkDigit';

/**
 * CUIT del modo "desde factura" (spec 009, DEC-APP-002): opcional, así que vacío es válido; si se carga, 11 dígitos (se
 * aceptan guiones, puntos, barras y espacios) con dígito verificador correcto.
 */
export const validateTaxId = (value: string | null | undefined): TaxIdErrorKey | undefined => {
    if (!(value ?? '').trim()) return undefined;
    const digits = normalizeTaxId(value);
    if (!digits || digits.length !== 11) return 'format';
    return isValidTaxId(digits) ? undefined : 'checkDigit';
};

/** Valores del alta desde factura (razón social normalizada; CUIT sólo dígitos y sólo si se cargó). */
export const buildInvoiceProviderValues = (businessName: string, taxId: string | null | undefined): { businessName: string; taxId?: string } => {
    const digits = normalizeTaxId(taxId);
    return digits ? { businessName: normalizeBusinessName(businessName), taxId: digits } : { businessName: normalizeBusinessName(businessName) };
};
